import { currentIdentity, invalidateIdentity, isCurrentIdentity } from './identity-state'
import type { PageMeta } from './types'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly meta: Record<string, unknown> | undefined

  constructor(status: number, code: string, message: string, meta?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.meta = meta
  }
}

export class UnknownOutcomeError extends Error {
  constructor() {
    super('请求已发出但结果未知，请先确认状态')
    this.name = 'UnknownOutcomeError'
  }
}

interface Envelope<T> {
  success: boolean
  data?: T
  meta?: Record<string, unknown>
  error?: { code: string; message: string } | string
}

export interface ApiResult<T> {
  data: T
  meta: Record<string, unknown> | undefined
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method?: Method
  body?: unknown
  signal?: AbortSignal
  idempotencyKey?: string
  maxResponseBytes?: number
  query?: Record<string, string | number | boolean | undefined | null>
}

const WRITE_TIMEOUT_MS = 15_000

export function buildPath(path: string, query?: RequestOptions['query']): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const method = options.method ?? 'GET'
  const isWrite = method !== 'GET'
  const identity = currentIdentity()
  const anonymousAccountFlow =
    /^\/api\/auth\/(login|register|registration-status|forgot-password|reset-password|verify-email)$/.test(
      path,
    )
  const headers = new Headers()
  if (options.body !== undefined) headers.set('Content-Type', 'application/json')
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey)
  if (identity.accountId && !anonymousAccountFlow) headers.set('X-Web-Identity', identity.accountId)
  const timeout = isWrite ? AbortSignal.timeout(WRITE_TIMEOUT_MS) : undefined
  const signal =
    options.signal && timeout ? AbortSignal.any([options.signal, timeout]) : (options.signal ?? timeout)

  let response: Response
  try {
    response = await fetch(buildPath(path, options.query), {
      method,
      credentials: 'include',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal,
    })
  } catch (error) {
    if (options.signal?.aborted) throw error
    if (isWrite) throw new UnknownOutcomeError()
    throw new ApiError(0, 'NETWORK', '网络连接失败')
  }

  let envelope: Envelope<T>
  try {
    if (options.maxResponseBytes && response.body) {
      const reader = response.body.getReader()
      const chunks: Uint8Array[] = []
      let size = 0
      try {
        for (;;) {
          const { value, done } = await reader.read()
          if (done) break
          size += value.length
          if (size > options.maxResponseBytes) throw new Error('response limit')
          chunks.push(value)
        }
      } finally {
        await reader.cancel().catch(() => {})
        reader.releaseLock()
      }
      const bytes = new Uint8Array(size)
      let offset = 0
      for (const chunk of chunks) {
        bytes.set(chunk, offset)
        offset += chunk.length
      }
      envelope = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)) as Envelope<T>
    } else envelope = (await response.json()) as Envelope<T>
  } catch {
    if (isWrite && response.ok) throw new UnknownOutcomeError()
    throw new ApiError(response.status, 'BAD_RESPONSE', '服务响应无法解析')
  }

  if (!isCurrentIdentity(identity)) {
    if (isWrite) throw new UnknownOutcomeError()
    throw new ApiError(409, 'IDENTITY_CHANGED', '账号已变化，请重新读取')
  }

  if (!response.ok || !envelope.success) {
    if (response.status === 401 && !anonymousAccountFlow) invalidateIdentity(identity)
    const err = envelope.error
    const code = typeof err === 'object' && err ? err.code : String(envelope.meta?.code ?? 'ERROR')
    const message = typeof err === 'object' && err ? err.message : (err ?? '请求失败')
    throw new ApiError(response.status, code, message, envelope.meta)
  }
  return { data: envelope.data as T, meta: envelope.meta }
}

export function pageMeta(meta: Record<string, unknown> | undefined): PageMeta {
  return {
    page: Number(meta?.page ?? 1),
    limit: Number(meta?.limit ?? 24),
    hasMore: Boolean(meta?.hasMore),
    total: typeof meta?.total === 'number' ? meta.total : null,
  }
}
